/**
 * Phase 19 F26: assessments are pediatric-first home health / PDN.
 *
 * The setting (home health / SNF / hospital / outpatient) and nursing level (skilled / PDN / LTAC)
 * selectors are gone: every save stores `settingType: 'home-health'` and `nursingLevel: 'private-duty'`
 * (the backend DEFAULT_FORM_CONFIG and the homebound rule expect them). The patient type is
 * pediatric unless the "Adult (transition, 18–21)" toggle is on; there is no geriatric option.
 * Assessments saved before F26 still open: a stored 'adult' or 'geriatric' patient type reads as
 * adult, and any stored setting or nursing level is replaced by the forced values.
 */

export type AssessmentPatientType = 'pediatric' | 'adult';

export const FORCED_SETTING_TYPE = 'home-health';
export const FORCED_NURSING_LEVEL = 'private-duty';

export const ADULT_TRANSITION_LABEL = 'Adult (transition, 18–21)';

/** 'adult' (or a legacy 'geriatric') reads as adult; anything else is pediatric. */
export function normalizePatientType(value: unknown): AssessmentPatientType {
  return value === 'adult' || value === 'geriatric' ? 'adult' : 'pediatric';
}

export interface PediatricFirstConfig {
  patientType: AssessmentPatientType;
  settingType: typeof FORCED_SETTING_TYPE;
  nursingLevel: typeof FORCED_NURSING_LEVEL;
}

/** The config with the forced setting / nursing level and a normalized patient type. */
export function withPediatricFirstConfig<T extends object>(config: T): Omit<T, keyof PediatricFirstConfig> & PediatricFirstConfig {
  const patientType = normalizePatientType((config as { patientType?: unknown }).patientType);
  return { ...config, patientType, settingType: FORCED_SETTING_TYPE, nursingLevel: FORCED_NURSING_LEVEL };
}

/** Short label for the collapsed configuration summary. */
export function patientTypeLabel(value: unknown): string {
  return normalizePatientType(value) === 'adult' ? ADULT_TRANSITION_LABEL : 'Pediatric';
}
